import { defineStore } from 'pinia'
import { markRaw, ref } from 'vue'
import { PersonalFinancialStatementNetworthForDateRangeResource } from './resources/personalFinancialStatementNetWorthForDateRangeResource'
import { PersonalFinancialStatementDepositBalanceAsOfDateResource } from './resources/personalFinancialStatementDepositBalanceAsOfDateResource'
import { PersonalFinancialStatementResourceType, PersonalFinancialStatementFetchOptions } from './resources/basePersonalFinancialStatementResource'
import { PersonalFinancialStatementHomeMetricsForDateRangeResource } from './resources/personalFinancialStatementHomeMetricsForDateRangeResource'
import { PersonalFinancialStatementTopMerchantsResource } from './resources/personalFinancialStatementTopMerchantsResource'
import { PersonalFinancialStatementMonthlySpendingComparisonResource } from './resources/personalFinancialStatementMonthlySpendingComparisonResource'
import { PersonalFinancialStatementLiabilityResource } from './resources/personalFinancialStatementLiabilityResource'
import { PersonalFinancialStatementBorrowingPowerResource } from './resources/personalFinancialStatementBorrowingPowerResource'
import { log } from '../../utils/logger'

const DEFAULT_RESOURCE_UPDATE_POLLING_DELAY_MS = 20_000
const DEFAULT_RESOURCE_UPDATE_POLLING_ITERATIONS = 6

export enum ConnectionType {
    PLAID = 'plaid',
    METHODFI = 'methodfi',
    NEW_HOME_ADDRESS = 'home', // This currently means setting an address or an address change but in the future it could mean adding an additional house
}

export const ConnectionTypeToDependentResource: Record<ConnectionType, PersonalFinancialStatementResourceType[]> = {
    [ConnectionType.PLAID]: [
        PersonalFinancialStatementResourceType.NET_WORTH_FOR_DATE_RANGE,
        PersonalFinancialStatementResourceType.DEPOSIT_BALANCE_AS_OF_DATE,
        PersonalFinancialStatementResourceType.TOP_MERCHANTS,
        PersonalFinancialStatementResourceType.MONTHLY_SPENDING_COMPARISON,
    ],
    [ConnectionType.METHODFI]: [
        PersonalFinancialStatementResourceType.NET_WORTH_FOR_DATE_RANGE,
        PersonalFinancialStatementResourceType.LIABILITIES,
        PersonalFinancialStatementResourceType.BORROWING_POWER,
    ],
    [ConnectionType.NEW_HOME_ADDRESS]: [PersonalFinancialStatementResourceType.HOME_VALUE_AND_EQUITY_FOR_DATE_RANGE],
}

export const usePersonalFinancialStatementStore = defineStore('personalFinancialStatement', () => {
    const activePollingConnectionTypes = ref<Set<ConnectionType>>(new Set())

    const netWorthResource = markRaw(new PersonalFinancialStatementNetworthForDateRangeResource())
    const depositBalanceResource = markRaw(new PersonalFinancialStatementDepositBalanceAsOfDateResource())
    const homeValueAndEquityResource = markRaw(new PersonalFinancialStatementHomeMetricsForDateRangeResource())
    const topMerchantsResource = markRaw(new PersonalFinancialStatementTopMerchantsResource())
    const monthlySpendingComparisonResource = markRaw(new PersonalFinancialStatementMonthlySpendingComparisonResource())
    const liabilitiesResource = markRaw(new PersonalFinancialStatementLiabilityResource())
    const borrowingPowerResource = markRaw(new PersonalFinancialStatementBorrowingPowerResource())

    // Mark raw so Pinia does not proxy the resource instances; their getters already
    // return refs and we want to avoid Vue auto-unwrapping them and breaking reactivity.
    const personalFinancialStatementResourceTypeToResourceRecord = markRaw({
        [PersonalFinancialStatementResourceType.NET_WORTH_FOR_DATE_RANGE]: netWorthResource,
        [PersonalFinancialStatementResourceType.DEPOSIT_BALANCE_AS_OF_DATE]: depositBalanceResource,
        [PersonalFinancialStatementResourceType.HOME_VALUE_AND_EQUITY_FOR_DATE_RANGE]: homeValueAndEquityResource,
        [PersonalFinancialStatementResourceType.TOP_MERCHANTS]: topMerchantsResource,
        [PersonalFinancialStatementResourceType.MONTHLY_SPENDING_COMPARISON]: monthlySpendingComparisonResource,
        [PersonalFinancialStatementResourceType.LIABILITIES]: liabilitiesResource,
        [PersonalFinancialStatementResourceType.BORROWING_POWER]: borrowingPowerResource,
    } as const)

    const updateDependentResources = (newConnectionTypes: ConnectionType[], options?: PersonalFinancialStatementFetchOptions): void => {
        log.info('usePersonalFinancialStatementStore.updateDependentResources: Updating dependent resources for connection types: %{connectionTypes}', {
            connectionTypes: newConnectionTypes.join(', '),
        })
        const resourceTypesToUpdate = new Set(newConnectionTypes.flatMap((connectionType) => ConnectionTypeToDependentResource[connectionType]))
        resourceTypesToUpdate.forEach((resourceType) => {
            personalFinancialStatementResourceTypeToResourceRecord[resourceType].maybeGetAndUpdateData(options)
        })
    }

    /**
     * @param newConnectionTypes - The connection types to update.
     * @param options - The options for the update.
     *
     * @remarks When to use onlyFetchBetweenMostRecentSnapshotAndCurrentDate (true vs false):
     * During initial account creation/ source connection, a large number of snapshots are being fetched and created concurrently.
     * We set onlyFetchBetweenMostRecentSnapshotAndCurrentDate to false to fetch the full
     * date range, since newly created snapshots may predate the most recent one and would
     * otherwise be missed. The probability of this is normally low since all information used for snapshot
     * creation is normally available. Thus, its worth it to take the risk of there being some missing snapshots
     * until the daily personalFinancialStatementUpdateAllStatementsJobQueue job runs for the decreased load on DB and decreased latency.
     */
    const updateDependentResourcesWithPolling = async (newConnectionTypes: ConnectionType[], options?: PersonalFinancialStatementFetchOptions): Promise<void> => {
        // Filter out connection types that are already being polled
        const connectionTypesToPoll = newConnectionTypes.filter((connectionType) => !activePollingConnectionTypes.value.has(connectionType))

        // If all requested connection types are already being polled, exit early
        if (connectionTypesToPoll.length === 0) {
            log.info('All requested connection types are already being polled: %{connectionTypes}', { connectionTypes: newConnectionTypes.join(', ') })
            return
        }

        try {
            // Add the new connection types to the active polling set
            connectionTypesToPoll.forEach((connectionType) => {
                activePollingConnectionTypes.value.add(connectionType)
            })
            // Trigger reactivity by cloning the Set
            activePollingConnectionTypes.value = new Set(activePollingConnectionTypes.value)

            for (let i = 0; i < DEFAULT_RESOURCE_UPDATE_POLLING_ITERATIONS; i++) {
                updateDependentResources(connectionTypesToPoll, options)
                if (i < DEFAULT_RESOURCE_UPDATE_POLLING_ITERATIONS - 1) {
                    await new Promise((resolve) => setTimeout(resolve, DEFAULT_RESOURCE_UPDATE_POLLING_DELAY_MS))
                }
            }
        } catch (error) {
            log.error('usePersonalFinancialStatementStore.updateDependentResourcesWithPolling: Error during resource polling:', { error })
        } finally {
            // Remove the connection types from the active polling set
            connectionTypesToPoll.forEach((connectionType) => {
                activePollingConnectionTypes.value.delete(connectionType)
            })
            // Trigger reactivity by cloning the Set
            activePollingConnectionTypes.value = new Set(activePollingConnectionTypes.value)
        }
    }

    return {
        activePollingConnectionTypes,
        personalFinancialStatementResourceTypeToResourceRecord,
        updateDependentResourcesWithPolling,
    }
})
